// Admin: detail of a quotation request. Same object (quotation_request) as store/cart; view and actions by status.
// Uses QuotationBlocksView (canonical from store/cart). PDF generation deprecated; digital flow only.

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useQuotationRequest,
  useAdminPatchQuotationRequest,
  useLeads,
  useQuotationHeaderImages,
  useCreateQuotationHeaderImage,
  useDeleteQuotationHeaderImage,
  useQuotationSealImages,
  useCreateQuotationSealImage,
  useDeleteQuotationSealImage,
  useCompanySettings,
  useCompanyBankAccounts,
  useQuotationRequestDocuments,
} from '@/hooks/use-store';
import {
  useSourcingItems,
  useBrands,
  useOrigins,
  useOriginPorts,
  useMoqUnits,
  useLoadingMethods,
  useSourcingItemFamilies,
  useProductFamilyMixOptions,
} from '@/hooks/use-store';
import { supabase } from '@/lib/supabase';
import { toCartLine } from '@/hooks/use-quotation-cart';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';
import type { QuotationRequestItem } from '@/lib/store-types';
import type { QuotationDocBlocks, RowLoadType } from '@/lib/store-types';
import { postWorkerApi, postWorkerApiWithAuth, postWorkerApiWithAuthFormData } from '@/lib/worker-api';
import { isLoadingMethodPalletized } from '@/lib/constants';
import { QuotationBlocksView } from '@/components/store/QuotationBlocksView';
import { QuotationAcceptanceView } from '@/components/store/QuotationAcceptanceView';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import type { QuotationRequestStatus, QuotationRequestDocumentType } from '@/lib/store-types';
import { normalizeQuotationRequestStatus } from '@/lib/store-types';
import { ArrowLeft, FileText, Link2, Copy, RefreshCw, FileDown, Upload, Check, X, Image as ImageIcon, MessageSquare, Download, Loader2, FileUp } from 'lucide-react';

/** Etiquetas de estado. "Enviado" y "Pendiente pago balance" unificados en un solo paso: "En camino y Pend. pago balance". */
const STATUS_LABELS: Record<QuotationRequestStatus, string> = {
  draft: 'Borrador',
  pending: 'Enviado',
  en_revision: 'En revisión',
  cotizado: 'Cotizado',
  finished: 'Finalizado',
  pending_deposit_payment: 'Pendiente pago depósito',
  in_process: 'En proceso',
  shipped: 'En camino y Pend. pago balance',
  pending_final_payment: 'En camino y Pend. pago balance',
  arriving_soon: 'En camino',
  delivered: 'Entregado',
};

/** Estados que el admin puede elegir en el desplegable (sin duplicar shipped/pending_final_payment). */
const SELECTABLE_STATUSES: QuotationRequestStatus[] = [
  'draft', 'pending', 'en_revision', 'cotizado', 'finished',
  'pending_deposit_payment', 'in_process', 'shipped', 'arriving_soon', 'delivered',
];

const DOCUMENT_TYPE_LABEL: Record<QuotationRequestDocumentType, string> = {
  pdf_quotation: 'PDF Cotización',
  pdf_proforma_invoice: 'Factura proforma',
  deposit_receipt: 'Comprobante de depósito',
  shipping_documents: 'Documentos de embarque',
  balance_payment_receipt: 'Comprobante de pago balance',
};

/** Tipos mostrados en la lista (sin PDF Cotización; el admin solo genera Factura proforma). */
const DISPLAY_DOCUMENT_TYPES: QuotationRequestDocumentType[] = [
  'pdf_proforma_invoice',
  'deposit_receipt',
  'shipping_documents',
  'balance_payment_receipt',
];

const PDF_GENERATABLE_TYPES: QuotationRequestDocumentType[] = ['pdf_proforma_invoice'];

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { dateStyle: 'medium' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

const LEAD_NONE_VALUE = '__none__';

export default function AdminQuotationRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: request, isLoading, error } = useQuotationRequest(id ?? null);
  const { data: leads } = useLeads();
  const { data: allSourcingItems } = useSourcingItems(null);
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: originPorts } = useOriginPorts();
  const { data: moqUnits } = useMoqUnits();
  const { data: loadingMethods } = useLoadingMethods();
  const { data: sourcingItemFamilies } = useSourcingItemFamilies();
  const { data: mixOptionsRows } = useProductFamilyMixOptions();
  const adminPatch = useAdminPatchQuotationRequest();
  const { toast } = useToast();
  const { data: companySettings } = useCompanySettings();
  const { data: bankAccounts } = useCompanyBankAccounts();
  const { data: requestDocuments, refetch: refetchRequestDocuments } = useQuotationRequestDocuments(id ?? null);
  const { data: headerImages } = useQuotationHeaderImages();
  const createHeaderImage = useCreateQuotationHeaderImage();
  const deleteHeaderImage = useDeleteQuotationHeaderImage();
  const { data: sealImages } = useQuotationSealImages();
  const createSealImage = useCreateQuotationSealImage();
  const deleteSealImage = useDeleteQuotationSealImage();

  // Actualización en tiempo real: cuando el cliente (o otra pestaña admin) cambia la cotización, refrescar el detalle.
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`admin_quotation_request:${id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'quotation_requests', filter: `id=eq.${id}` },
        () => queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_request', id] })
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, queryClient]);

  /** Info general del documento (muestra de oferta digital): title, header, seal, final message. */
  const [documentTitle, setDocumentTitle] = useState('');
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [sealImageUrl, setSealImageUrl] = useState('');
  const [showFinalMessage, setShowFinalMessage] = useState(false);
  const [finalMessage, setFinalMessage] = useState('');
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingSeal, setUploadingSeal] = useState(false);

  useEffect(() => {
    if (!request) return;
    setDocumentTitle(request.document_title ?? 'Cotización');
    // Si la solicitud no tiene cabecera/sello, usar por defecto los de company_settings para que el admin vea lo mismo que el cliente.
    setHeaderImageUrl(request.header_image_url ?? companySettings?.header_image_url ?? '');
    setSealImageUrl(request.seal_image_url ?? companySettings?.seal_image_url ?? '');
    setShowFinalMessage(request.show_final_message ?? false);
    setFinalMessage(request.final_message ?? '');
  }, [request, companySettings]);

  const patchDocumentDisplay = useCallback(
    (patch: {
      document_title?: string | null;
      header_image_url?: string | null;
      seal_image_url?: string | null;
      final_message?: string | null;
      show_final_message?: boolean | null;
    }) => {
      if (!request) return;
      adminPatch.mutate(
        { requestId: request.id, patch },
        {
          onSuccess: () => toast({ title: 'Guardado' }),
          onError: (e) => toast({ title: 'Error al guardar', description: String(e), variant: 'destructive' }),
        }
      );
    },
    [request, adminPatch, toast]
  );

  const handleHeaderUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !request) return;
      setUploadingHeader(true);
      try {
        const uid = crypto.randomUUID().slice(0, 8);
        const ext = file.name.split('.').pop() || 'png';
        const cleanName = file.name.replace(/\.[^/.]+$/, '').slice(0, 40);
        const path = `quotation-docs/headers/${uid}.${ext}`;
        const { error } = await supabase.storage.from('store-assets').upload(path, file, { upsert: true });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('store-assets').getPublicUrl(path);
        await createHeaderImage.mutateAsync({ name: cleanName || 'Cabecera', url: urlData.publicUrl });
        setHeaderImageUrl(urlData.publicUrl);
        patchDocumentDisplay({ header_image_url: urlData.publicUrl });
        toast({ title: 'Imagen de cabecera subida' });
      } catch (err) {
        toast({ title: 'Error subiendo imagen', description: String(err), variant: 'destructive' });
      } finally {
        setUploadingHeader(false);
      }
    },
    [request, createHeaderImage, patchDocumentDisplay, toast]
  );

  const handleSealUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !request) return;
      setUploadingSeal(true);
      try {
        const uid = crypto.randomUUID().slice(0, 8);
        const ext = file.name.split('.').pop() || 'png';
        const cleanName = file.name.replace(/\.[^/.]+$/, '').slice(0, 40);
        const path = `quotation-docs/seals/${uid}.${ext}`;
        const { error } = await supabase.storage.from('store-assets').upload(path, file, { upsert: true });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('store-assets').getPublicUrl(path);
        await createSealImage.mutateAsync({ name: cleanName || 'Sello', url: urlData.publicUrl });
        setSealImageUrl(urlData.publicUrl);
        patchDocumentDisplay({ seal_image_url: urlData.publicUrl });
        toast({ title: 'Sello subido' });
      } catch (err) {
        toast({ title: 'Error subiendo sello', description: String(err), variant: 'destructive' });
      } finally {
        setUploadingSeal(false);
      }
    },
    [request, createSealImage, patchDocumentDisplay, toast]
  );

  const itemIdToFamilyId = useMemo(() => {
    if (!sourcingItemFamilies?.length) return undefined;
    const map: Record<string, string> = {};
    sourcingItemFamilies.forEach((r) => {
      if (map[r.item_id] == null) map[r.item_id] = r.family_id;
    });
    return map;
  }, [sourcingItemFamilies]);

  const currentStatus = request ? normalizeQuotationRequestStatus(request.status) : 'draft';
  const items = (request?.items ?? []) as QuotationRequestItem[];

  /** Cart lines for QuotationBlocksView (same shape as store/cart). */
  const cartLines = useMemo(() => {
    if (!request?.items?.length) return [];
    return items.map((it) =>
      toCartLine(
        { ...it, moq_value: (it as { moq_value?: number }).moq_value },
        allSourcingItems ?? [],
        brands ?? [],
        origins ?? [],
        moqUnits ?? []
      )
    );
  }, [request?.items, items, allSourcingItems, brands, origins, moqUnits]);

  const handlePatchBlocks = useCallback(
    (blocks: QuotationDocBlocks) => {
      if (!request) return;
      adminPatch.mutate(
        { requestId: request.id, patch: { blocks } },
        {
          onSuccess: () => toast({ title: 'Condiciones actualizadas' }),
          onError: (e) => toast({ title: 'Error al guardar', description: String(e), variant: 'destructive' }),
        }
      );
    },
    [request, adminPatch, toast]
  );

  /** Invalidate admin detail so UI refetches after worker API calls (add/update/remove). */
  const invalidateDetail = useCallback(() => {
    if (request?.id) queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_request', request.id] });
  }, [request?.id, queryClient]);

  /** Client-like props for en_revision: add products, update quantity, remove, patch blocks (same UX as store/cart). */
  const adminClientProps = useMemo(() => {
    if (!request?.id) return null;
    const patchBlocksAsync = (blocks: QuotationDocBlocks) =>
      new Promise<void>((resolve, reject) => {
        adminPatch.mutate(
          { requestId: request.id, patch: { blocks } },
          {
            onSuccess: () => {
              toast({ title: 'Guardado' });
              resolve();
            },
            onError: (e) => {
              toast({ title: 'Error al guardar', description: String(e), variant: 'destructive' });
              reject(e);
            },
          }
        );
      });
    const getDefaultLoadType = (sourcingItemId: string): RowLoadType => {
      const item = allSourcingItems?.find((i) => i.id === sourcingItemId);
      const methodName = loadingMethods?.find((m) => m.id === item?.loading_method_id)?.name;
      return isLoadingMethodPalletized(methodName) ? 'pallets' : 'floor';
    };
    const updateMixRowLoadType = (blockIndex: number, rowIndex: number, value: RowLoadType) => {
      if (!request?.blocks?.mix?.[blockIndex]) return;
      const mix = [...request.blocks.mix];
      const block = { ...mix[blockIndex], rows: [...(mix[blockIndex].rows ?? [])] };
      const row = block.rows[rowIndex];
      if (!row) return;
      block.rows[rowIndex] = { ...row, row_load_type: value };
      mix[blockIndex] = block;
      handlePatchBlocks({ ...request.blocks, mix });
    };
    return {
      patchBlocks: patchBlocksAsync,
      add: async (line: QuotationCartLine, options?: { mixBlockIndex?: number; forceNewContainer?: boolean }) => {
        const body: Record<string, unknown> = {
          request_id: request.id,
          sourcing_item_id: line.sourcingItemId,
          quantity: line.quantity,
          moq_unit: line.moqUnitLabel,
          moq_value: line.moqValue,
          name: line.name,
          slug: line.slug,
        };
        if (typeof options?.mixBlockIndex === 'number') body.mix_block_index = options.mixBlockIndex;
        if (options?.forceNewContainer === true) body.force_new_container = true;
        const res = await postWorkerApi('/api/quotation-request/add-item', body);
        if (!res.ok) {
          toast({ title: 'Error al añadir', description: res.error ?? 'Error', variant: 'destructive' });
          return;
        }
        invalidateDetail();
      },
      updateQuantity: async (sourcingItemId: string, q: number) => {
        const res = await postWorkerApi('/api/quotation-request/update-quantity', {
          request_id: request.id,
          sourcing_item_id: sourcingItemId,
          quantity: q,
        });
        if (!res.ok) {
          toast({ title: 'Error al actualizar', description: res.error ?? 'Error', variant: 'destructive' });
          return;
        }
        invalidateDetail();
      },
      remove: async (sourcingItemId: string) => {
        const res = await postWorkerApi('/api/quotation-request/remove-item', {
          request_id: request.id,
          sourcing_item_id: sourcingItemId,
        });
        if (!res.ok) {
          toast({ title: 'Error al quitar', description: res.error ?? 'Error', variant: 'destructive' });
          return;
        }
        invalidateDetail();
      },
      toast: (t: { title: string; description?: string; variant?: 'destructive' }) => toast(t),
      getDefaultLoadType,
      updateMixRowLoadType,
      allSourcingItems: allSourcingItems ?? [],
      loadingMethods: loadingMethods ?? [],
      brands: brands ?? [],
      origins: origins ?? [],
      originPorts: originPorts ?? [],
      moqUnits: moqUnits ?? [],
      itemIdToFamilyId,
      mixOptions: mixOptionsRows ?? undefined,
    };
  }, [
    request?.id,
    request?.blocks,
    adminPatch,
    toast,
    handlePatchBlocks,
    invalidateDetail,
    allSourcingItems,
    loadingMethods,
    brands,
    origins,
    originPorts,
    moqUnits,
    itemIdToFamilyId,
    mixOptionsRows,
  ]);

  const handleStatusChange = (newStatus: QuotationRequestStatus) => {
    if (!request) return;

    // Patch principal: cambio de estado
    adminPatch.mutate(
      { requestId: request.id, patch: { status: newStatus } },
      {
        onSuccess: async () => {
          // Regla de negocio: al pasar a cotizado, si hay lead y no se han rellenado
          // explícitamente los campos de destinatario, los completamos desde el lead
          // para que queden persistidos en la solicitud/cotización.
          if (
            newStatus === 'cotizado' &&
            request.lead &&
            (!request.addressee_company || !request.addressee_contact)
          ) {
            const nextPatch: Record<string, string | null> = {};
            if (!request.addressee_company && request.lead.company) {
              nextPatch.addressee_company = request.lead.company;
            }
            if (!request.addressee_contact && request.lead.name) {
              nextPatch.addressee_contact = request.lead.name;
            }
            if (Object.keys(nextPatch).length > 0) {
              adminPatch.mutate({ requestId: request.id, patch: nextPatch });
            }
          }
          toast({ title: 'Estado actualizado' });
          // Notificar al equipo cuando se pasa a "En proceso" o "Enviado" (admin cambia estado desde el panel)
          if (newStatus === 'in_process') {
            const notifRes = await postWorkerApi<{ sent?: boolean }>('/api/quotation-request/notify-in-process', {
              request_id: request.id,
            });
            if (notifRes.ok && notifRes.data?.sent) {
              toast({ title: 'Email enviado al equipo', description: 'Se notificó que la solicitud está en proceso.' });
            }
          }
          if (newStatus === 'shipped') {
            const notifRes = await postWorkerApi<{ sent?: boolean }>('/api/quotation-request/notify-shipped', {
              request_id: request.id,
            });
            if (notifRes.ok && notifRes.data?.sent) {
              toast({ title: 'Email enviado al equipo', description: 'Se notificó que la solicitud está enviada.' });
            }
          }
          // Enviar email al cliente: orden lista / cotización esperando aprobación
          if (newStatus === 'cotizado') {
            const res = await postWorkerApi<{ sent?: boolean }>('/api/quotation-request/notify-cotizado', {
              request_id: request.id,
            });
            if (res.ok && res.data?.sent) {
              toast({ title: 'Email enviado al cliente', description: 'Se notificó que la cotización está lista.' });
            } else if (!res.ok) {
              toast({ title: 'No se pudo enviar el email al cliente', variant: 'destructive' });
            }
            // Solo generar PDF cotización en segundo plano cuando se pasa de "en revisión" a "cotizado".
            // Si se pasa de "pending" a "cotizado" (p. ej. desde el desplegable), no se genera; admin/cliente pueden generarlo manualmente con "Descargar PDF cotización".
            if (currentStatus === 'en_revision') {
              postWorkerApiWithAuth('/api/generate-request-pdf', { request_id: request.id, variant: 'quotation' }, getSession)
                .then((r) => {
                  if (r.ok) queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_request', request.id] });
                })
                .catch(() => {
                  toast({
                    title: 'No se pudo generar el PDF en segundo plano',
                    description: 'Usa "Descargar PDF cotización" más abajo para generarlo manualmente.',
                    variant: 'destructive',
                  });
                });
            }
          }
        },
        onError: (e) => toast({ title: 'Error al actualizar estado', description: String(e), variant: 'destructive' }),
      }
    );
  };

  const handleAssignLead = (leadId: string) => {
    if (!request) return;
    const value = leadId === LEAD_NONE_VALUE || leadId === '' ? null : leadId;
    adminPatch.mutate(
      { requestId: request.id, patch: { lead_id: value } },
      {
        onSuccess: () => toast({ title: value ? 'Lead asignado' : 'Lead desasignado' }),
        onError: (e) => toast({ title: 'Error', description: String(e), variant: 'destructive' }),
      }
    );
  };

  const catalogBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = request?.share_token ? `${catalogBaseUrl}/store/catalog/${request.share_token}` : '';

  const handleGenerateShareLink = () => {
    if (!request) return;
    const token = crypto.randomUUID();
    adminPatch.mutate(
      { requestId: request.id, patch: { share_token: token } },
      {
        onSuccess: () => toast({ title: 'Link generado', description: 'Copia y comparte el enlace del catálogo.' }),
        onError: (e) => toast({ title: 'Error al generar link', description: String(e), variant: 'destructive' }),
      }
    );
  };

  const getSession = useCallback(() => supabase.auth.getSession(), []);
  const [docUploadPending, setDocUploadPending] = useState<QuotationRequestDocumentType | null>(null);
  const [docGeneratePending, setDocGeneratePending] = useState<'quotation' | 'proforma_invoice' | null>(null);
  const [pdfQuotationDownloadPending, setPdfQuotationDownloadPending] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleBankAccountChange = (value: string) => {
    if (!request) return;
    const v = value === '__none__' || value === '' ? null : value;
    adminPatch.mutate(
      { requestId: request.id, patch: { bank_account_id: v } },
      { onSuccess: () => toast({ title: 'Cuenta bancaria actualizada' }), onError: (e) => toast({ title: 'Error', description: String(e), variant: 'destructive' }) }
    );
  };

  const handleEtdChange = (value: string) => {
    if (!request) return;
    adminPatch.mutate(
      { requestId: request.id, patch: { etd_origin_port: value || null } },
      { onSuccess: () => toast({ title: 'ETD actualizado' }), onError: (e) => toast({ title: 'Error', description: String(e), variant: 'destructive' }) }
    );
  };

  const handleEtaChange = (value: string) => {
    if (!request) return;
    adminPatch.mutate(
      { requestId: request.id, patch: { eta_destination_port: value || null } },
      { onSuccess: () => toast({ title: 'ETA actualizado' }), onError: (e) => toast({ title: 'Error', description: String(e), variant: 'destructive' }) }
    );
  };

  const handleGenerateRequestPdf = useCallback(
    async (variant: 'quotation' | 'proforma_invoice') => {
      if (!request?.id) return;
      setDocGeneratePending(variant);
      const res = await postWorkerApiWithAuth('/api/generate-request-pdf', { request_id: request.id, variant }, getSession);
      setDocGeneratePending(null);
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_request', request.id] });
        refetchRequestDocuments();
        toast({ title: 'PDF generado' });
      } else {
        toast({ title: res.error ?? 'Error al generar PDF', variant: 'destructive' });
      }
    },
    [request?.id, getSession, queryClient, refetchRequestDocuments, toast]
  );

  /** Descargar PDF cotización: si ya existe (generado al pasar a cotizado), abrir; si no, generarlo y abrir. */
  const handleDownloadQuotationPdf = useCallback(async (): Promise<string | null> => {
    if (!request?.id) return null;
    const docs = requestDocuments ?? [];
    const latestQuotation = docs
      .filter((d) => d.document_type === 'pdf_quotation')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    if (latestQuotation?.file_url) {
      return latestQuotation.file_url;
    }
    setPdfQuotationDownloadPending(true);
    const res = await postWorkerApiWithAuth<{ pdf_url?: string }>(
      '/api/generate-request-pdf',
      { request_id: request.id, variant: 'quotation' },
      getSession
    );
    setPdfQuotationDownloadPending(false);
    if (res.ok && res.data?.pdf_url) {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_request', request.id] });
      refetchRequestDocuments();
      return res.data.pdf_url;
    }
    toast({ title: res.ok ? 'No se devolvió la URL del PDF' : (res.error ?? 'Error al generar PDF'), variant: 'destructive' });
    return null;
  }, [request?.id, requestDocuments, getSession, queryClient, refetchRequestDocuments, toast]);

  const handleUploadRequestDocument = useCallback(
    async (documentType: QuotationRequestDocumentType, file: File) => {
      if (!request?.id) return;
      setDocUploadPending(documentType);
      const formData = new FormData();
      formData.set('request_id', request.id);
      formData.set('document_type', documentType);
      formData.append('file', file);
      const res = await postWorkerApiWithAuthFormData<{ file_url?: string; document_id?: string }>(
        '/api/quotation-request/upload-document',
        formData,
        getSession
      );
      setDocUploadPending(null);
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_request', request.id] });
        refetchRequestDocuments();
        toast({ title: 'Documento subido' });
      } else {
        toast({ title: res.error ?? 'Error al subir', variant: 'destructive' });
      }
    },
    [request?.id, getSession, queryClient, refetchRequestDocuments, toast]
  );

  const selectedBankAccount = request?.bank_account_id && bankAccounts ? bankAccounts.find((b) => b.id === request.bank_account_id) : null;
  const showEtdEta = currentStatus === 'shipped' || currentStatus === 'arriving_soon' || currentStatus === 'delivered';

  const handleCopyShareLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast({ title: 'Enlace copiado' }),
      () => toast({ title: 'No se pudo copiar', variant: 'destructive' })
    );
  };

  /** Patch document-level fields (datos de cotización). Used when en_revision. */
  const patchDocumentField = useCallback(
    (field: keyof Pick<
      typeof request,
      'addressee_company' | 'addressee_contact' | 'incoterm' | 'destination_port_or_address' |
      'final_price_text' | 'payment_type' | 'payment_method' | 'observaciones'
    >, value: string | null) => {
      if (!request) return;
      adminPatch.mutate(
        { requestId: request.id, patch: { [field]: value ?? null } },
        {
          onSuccess: () => toast({ title: 'Guardado' }),
          onError: (e) => toast({ title: 'Error al guardar', description: String(e), variant: 'destructive' }),
        }
      );
    },
    [request, adminPatch, toast]
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/store/admin/quotation-requests">← Solicitudes de cotización</Link>
        </Button>
        <p className="text-zinc-500">Cargando...</p>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/store/admin/quotation-requests">← Solicitudes de cotización</Link>
        </Button>
        <p className="text-zinc-600">Solicitud no encontrada.</p>
      </div>
    );
  }

  const lead = request.lead;
  const isDraft = currentStatus === 'draft';
  const isPending = currentStatus === 'pending';
  const isEnRevision = currentStatus === 'en_revision';
  const isCotizado = currentStatus === 'cotizado';
  const isFinished = currentStatus === 'finished';
  const isPostAccept = ['pending_deposit_payment', 'in_process', 'shipped', 'pending_final_payment', 'arriving_soon', 'delivered'].includes(currentStatus);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild className="gap-1">
          <Link to="/store/admin/quotation-requests">
            <ArrowLeft className="h-4 w-4" />
            Solicitudes de cotización
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">
            Detalle de solicitud de cotización · #{request.id.slice(-8).toUpperCase()}
          </h1>
          <p className="text-zinc-600 mt-1">{formatDate(request.created_at)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-zinc-600">Estado</Label>
            <Select
              value={currentStatus === 'pending_final_payment' ? 'shipped' : currentStatus}
              onValueChange={(v) => handleStatusChange(v as QuotationRequestStatus)}
              disabled={adminPatch.isPending}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SELECTABLE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isPending && (
            <Button
              type="button"
              onClick={() => handleStatusChange('en_revision')}
              disabled={adminPatch.isPending}
              className="gap-2"
            >
              Iniciar revisión
            </Button>
          )}
          {isEnRevision && (
            <Button
              type="button"
              onClick={() => handleStatusChange('cotizado')}
              disabled={adminPatch.isPending}
              className="gap-2"
            >
              Marcar como cotizado
            </Button>
          )}
          {showEtdEta && (
            <>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-zinc-600">ETD puerto origen</Label>
                <Input
                  type="date"
                  className="w-[140px]"
                  value={(request.etd_origin_port as string)?.slice(0, 10) ?? ''}
                  onChange={(e) => handleEtdChange(e.target.value || '')}
                  disabled={adminPatch.isPending}
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-zinc-600">ETA puerto destino</Label>
                <Input
                  type="date"
                  className="w-[140px]"
                  value={(request.eta_destination_port as string)?.slice(0, 10) ?? ''}
                  onChange={(e) => handleEtaChange(e.target.value || '')}
                  disabled={adminPatch.isPending}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Referencia a cuenta asignada (detalle en Config empresa). */}
      {selectedBankAccount && (
        <p className="text-sm text-zinc-600">
          Cuenta asignada: <span className="font-medium text-zinc-800">{selectedBankAccount.label ?? selectedBankAccount.bank_name ?? 'Cuenta'}</span>
          {' · '}
          <Link to="/store/admin/company-settings" className="text-primary hover:underline">Ver en Config empresa</Link>
        </p>
      )}

      {/* Mensaje del cliente al pedir cambios (vuelve a en_revision) */}
      {request.revision_request_comment && (
        <Card className="border-amber-200 bg-amber-50/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-900">
              <MessageSquare className="h-4 w-4" />
              El cliente solicitó cambios
            </CardTitle>
            <p className="text-sm text-amber-800 font-normal mt-1">
              Mensaje del cliente al usar &quot;Pedir cambio&quot; en la cotización:
            </p>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-900 whitespace-pre-wrap rounded-lg border border-amber-200 bg-white/60 p-4">
              {request.revision_request_comment}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Tarjeta de datos de cotización: solo en en_revision. */}
        {isEnRevision && (
          <Card className="border-zinc-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Datos de cotización</CardTitle>
              <p className="text-sm text-zinc-500 font-normal">
                Completa o edita para la cotización al cliente.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <>
                  {!lead && (
                    <>
                      <div>
                        <Label className="text-xs">Empresa destinataria</Label>
                        <Input
                          className="mt-1"
                          placeholder="Nombre de la empresa"
                          defaultValue={request.addressee_company ?? ''}
                          onBlur={(e) => patchDocumentField('addressee_company', e.target.value.trim() || null)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Persona de contacto</Label>
                        <Input
                          className="mt-1"
                          placeholder="Nombre del contacto"
                          defaultValue={request.addressee_contact ?? ''}
                          onBlur={(e) => patchDocumentField('addressee_contact', e.target.value.trim() || null)}
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <Label className="text-xs">Incoterm</Label>
                    <Input
                      className="mt-1"
                      placeholder="CIF, DDP, FOB..."
                      defaultValue={request.incoterm ?? ''}
                      onBlur={(e) => patchDocumentField('incoterm', e.target.value.trim() || null)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Puerto o dirección de destino</Label>
                    <Input
                      className="mt-1"
                      placeholder="Puerto de destino o dirección"
                      defaultValue={request.destination_port_or_address ?? ''}
                      onBlur={(e) => patchDocumentField('destination_port_or_address', e.target.value.trim() || null)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Precio final total</Label>
                    <Input
                      className="mt-1"
                      placeholder="$12,500 USD o a consultar"
                      defaultValue={request.final_price_text ?? ''}
                      onBlur={(e) => patchDocumentField('final_price_text', e.target.value.trim() || null)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo de pago</Label>
                    <Input
                      className="mt-1"
                      placeholder="30% depósito, 70% contra BL"
                      defaultValue={request.payment_type ?? ''}
                      onBlur={(e) => patchDocumentField('payment_type', e.target.value.trim() || null)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Método de pago</Label>
                    <Input
                      className="mt-1"
                      placeholder="Transferencia, crypto..."
                      defaultValue={request.payment_method ?? ''}
                      onBlur={(e) => patchDocumentField('payment_method', e.target.value.trim() || null)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Observaciones</Label>
                    <Textarea
                      className="mt-1 min-h-[80px]"
                      placeholder="Notas adicionales, condiciones especiales..."
                      defaultValue={request.observaciones ?? ''}
                      onBlur={(e) => patchDocumentField('observaciones', e.target.value.trim() || null)}
                    />
                  </div>
                  {request.specifications && (
                    <div className="pt-2 border-t border-zinc-100">
                      <Label className="text-xs text-zinc-500">Especificaciones</Label>
                      <p className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap">{request.specifications}</p>
                    </div>
                  )}
                </>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Columna lateral: Lead, cuenta bancaria y link compartido. */}
        <Card className="border-zinc-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Lead, cuenta y link
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Lead</Label>
              <Select value={request.lead_id ?? LEAD_NONE_VALUE} onValueChange={handleAssignLead} disabled={adminPatch.isPending}>
                <SelectTrigger>
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LEAD_NONE_VALUE}>Sin asignar</SelectItem>
                  {(leads ?? []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name ?? l.email} ({l.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selector de cuenta solo desde en_revision en adelante (en draft/pending se oculta). */}
            {!(isDraft || isPending) && (
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500">Cuenta bancaria</Label>
                <Select
                  value={request.bank_account_id ?? '__none__'}
                  onValueChange={handleBankAccountChange}
                  disabled={adminPatch.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin asignar</SelectItem>
                    {(bankAccounts ?? []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.label ?? b.bank_name ?? b.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-3 pt-2 border-t border-zinc-100">
              {request.lead_id && (
                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Si hay un lead asignado, solo ese lead podrá abrir el enlace (debe iniciar sesión).
                </p>
              )}
              {request.share_token ? (
                <div className="flex flex-wrap items-center gap-2">
                  <code className="flex-1 min-w-0 text-xs bg-zinc-100 rounded px-2 py-1.5 truncate" title={shareUrl}>
                    {shareUrl}
                  </code>
                  <Button type="button" variant="outline" size="sm" className="gap-1" onClick={handleCopyShareLink}>
                    <Copy className="h-3.5 w-3.5" />
                    Copiar
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="gap-1" onClick={handleGenerateShareLink} disabled={adminPatch.isPending}>
                    <RefreshCw className="h-3.5 w-3.5" />
                    Regenerar
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" className="gap-2" onClick={handleGenerateShareLink} disabled={adminPatch.isPending}>
                  <Link2 className="h-4 w-4" />
                  Generar link compartido
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documentación: disponible desde en_revision; en draft/pending se muestra deshabilitada. */}
      <Card className={`border-zinc-200 ${(isDraft || isPending) ? 'opacity-75' : ''}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Documentación</CardTitle>
          <p className="text-xs text-zinc-500 mt-0.5">
            {(isDraft || isPending)
              ? 'Disponible al iniciar revisión.'
              : 'Generar factura proforma o subir documentos. Los comprobantes de depósito y balance los sube el cliente.'}
          </p>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {DISPLAY_DOCUMENT_TYPES.map((docType) => {
            const docsOfType = (requestDocuments ?? []).filter((d) => d.document_type === docType);
            const latest = docsOfType.length > 0
              ? docsOfType.reduce((a, b) => (new Date(b.created_at).getTime() > new Date(a.created_at).getTime() ? b : a))
              : null;
            const label = DOCUMENT_TYPE_LABEL[docType];
            const canGenerate = PDF_GENERATABLE_TYPES.includes(docType);
            const uploadPending = docUploadPending === docType;
            const generatePending = docGeneratePending === 'proforma_invoice';
            const docDisabled = isDraft || isPending;
            return (
              <div key={docType} className="flex flex-wrap items-center justify-between gap-2 rounded border border-zinc-100 bg-zinc-50/50 p-3">
                <div className="min-w-0">
                  <p className="font-medium text-zinc-800 text-sm">{label}</p>
                  {latest ? (
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {latest.file_name || 'Documento'} · {new Date(latest.created_at).toLocaleDateString('es-ES', { dateStyle: 'short' })}
                    </p>
                  ) : (
                    <p className="text-xs text-zinc-500 mt-0.5">Sin documento</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {latest && (
                    docDisabled ? (
                      <Button variant="outline" size="sm" className="gap-1" disabled>
                        <Download className="h-3.5 w-3.5" />
                        Descargar
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" asChild className="gap-1">
                        <a href={latest.file_url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-3.5 w-3.5" />
                          Descargar
                        </a>
                      </Button>
                    )
                  )}
                  {canGenerate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGenerateRequestPdf('proforma_invoice')}
                      disabled={!!generatePending || docDisabled}
                      className="gap-1"
                    >
                      {generatePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
                      Generar
                    </Button>
                  )}
                  <input
                    type="file"
                    ref={(el) => { fileInputRefs.current[docType] = el; }}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadRequestDocument(docType, file);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRefs.current[docType]?.click()}
                    disabled={!!uploadPending || docDisabled}
                    className="gap-1"
                  >
                    {uploadPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    Subir
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Bloque principal: misma vista que store/cart (QuotationBlocksView) */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Solicitud (misma vista que store/cart)</CardTitle>
          {isDraft && (
            <p className="text-sm text-zinc-500 mt-1">Puedes montar los bloques y contenedores del preview. El cliente aún no ha enviado la solicitud.</p>
          )}
        </CardHeader>
        <CardContent className="min-w-0 overflow-x-auto">
          {(isCotizado || isPostAccept) ? (
            <QuotationAcceptanceView
              requestId={request.id}
              blocks={request.blocks}
              items={cartLines}
              status={currentStatus}
              lead={request.lead}
              incoterm={request.incoterm}
              destinationPortOrAddress={request.destination_port_or_address}
              addressee_company={request.addressee_company}
              addressee_contact={request.addressee_contact}
              final_price_text={request.final_price_text}
              payment_type={request.payment_type}
              payment_method={request.payment_method}
              observaciones={request.observaciones}
              documentTitle={request.document_title}
              headerImageUrl={request.header_image_url}
              sealImageUrl={request.seal_image_url}
              isAdminView
              finalMessage={request.final_message}
              showFinalMessage={request.show_final_message}
              quotationCode={request.id?.replace(/-/g, '').slice(0, 8).toUpperCase()}
              showActions={false}
              responded={isFinished || isPostAccept}
              outcome={request.outcome ?? undefined}
              allSourcingItems={allSourcingItems ?? []}
              loadingMethods={loadingMethods ?? []}
              onDownloadQuotationPdf={handleDownloadQuotationPdf}
              pdfDownloadPending={pdfQuotationDownloadPending}
            />
          ) : (
            <QuotationBlocksView
              request={request}
              items={cartLines}
              mode={(isEnRevision || isDraft) ? 'admin-edit' : 'admin-view'}
              readOnly={!(isEnRevision || isDraft)}
              clientProps={(isEnRevision || isDraft) && adminClientProps ? adminClientProps : undefined}
              adminProps={{
                onPatchBlocks: (isEnRevision || isDraft) ? handlePatchBlocks : undefined,
                allSourcingItems: allSourcingItems ?? [],
                loadingMethods: loadingMethods ?? [],
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
